import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Palette, LayoutGrid, Sofa, PaintBucket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useSEO } from '../../hooks/useSEO';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/7z3is2BCgabuUutCmURgeJ/_DSC8265.webp';
const DETAIL_IMAGE_1 = 'https://cdn.magicpatterns.com/uploads/vBzCByw2N7rP6MS6jLeNiZ/_DSC8493.webp';
const DETAIL_IMAGE_2 = 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp';
export function CustomizationPage() {
  useSEO({
    title: '16,000 Customization Options — Make It Yours',
    description: 'Create a truly personalized Robeta camper van. Choose from over 16,000 configuration options including colors, materials, layouts, and equipment packages.'
  });
  return <main className="bg-white min-h-screen" data-id="element-945">
      {/* 1. FULL-SCREEN HERO */}
      <section className="relative min-h-screen w-full flex flex-col justify-between overflow-hidden" data-id="element-946">
        <div className="absolute inset-0 z-0" data-id="element-947">
          <img src={HERO_IMAGE} alt="Customization Hero" className="w-full h-full object-cover" data-id="element-948" />
          <div className="absolute inset-0 bg-black/50" data-id="element-949" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" data-id="element-950" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pt-32" data-id="element-951">
          <Breadcrumbs items={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Robeta Difference',
          href: '/robeta-difference'
        }, {
          label: 'Customization'
        }]} variant="light" data-id="element-952" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pb-24 md:pb-32" data-id="element-953">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl" data-id="element-954">
            <span className="text-robeta-copper text-sm font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-955">
              Your Van, Your Style
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-editorial leading-[0.9] mb-8" data-id="element-956">
              16,000
              <br data-id="element-957" />
              Options
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-2xl leading-relaxed" data-id="element-958">
              No two travelers are alike. Why should their vans be?
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. INTRO SECTION */}
      <section className="py-24 md:py-32 bg-white" data-id="element-959">
        <div className="container mx-auto px-6 md:px-12" data-id="element-960">
          <div className="max-w-4xl mx-auto text-center" data-id="element-961">
            <h2 className="text-3xl md:text-5xl font-light text-robeta-black leading-tight mb-8" data-id="element-962">
              "We don't believe in standard. We believe in{' '}
              <span className="italic font-serif text-robeta-copper" data-id="element-963">
                yours
              </span>
              ."
            </h2>
            <p className="text-lg text-robeta-black/60 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-964">
              Mass production is easy. Customization is hard. That's why most
              manufacturers offer you three choices of beige. At Robeta, we
              offer a staggering array of wood finishes, upholstery fabrics,
              exterior colors, and layout configurations. With over 16,000
              possible combinations, your Robeta will be as unique as your
              fingerprint.
            </p>
          </div>
        </div>
      </section>

      {/* 3. DEEP DIVE 1: LIMITLESS POSSIBILITIES */}
      <section className="py-20 md:py-32 bg-robeta-mist/10" data-id="element-965">
        <div className="container mx-auto px-6 md:px-12" data-id="element-966">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-967">
            <div className="order-2 lg:order-1" data-id="element-968">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-969">
                Design Freedom
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-970">
                A Palette of Possibilities
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-971">
                <p data-id="element-972">
                  Start with the wood. Do you prefer the modern warmth of "Oak
                  Natural" or the sleek sophistication of "Grey Stone"? Then,
                  move to the upholstery. Choose from durable fabrics, vegan
                  leathers, or premium genuine leather in dozens of colors.
                </p>
                <p data-id="element-973">
                  But it goes deeper than aesthetics. You can configure your
                  kitchen with different stove layouts. You can choose between a
                  standard bathroom or our innovative "Air" bathroom with a
                  swinging wall. You can add extra windows, skylights, or delete
                  them for privacy.
                </p>
                <p data-id="element-974">
                  We build your van to order, ensuring it fits your lifestyle
                  perfectly.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative aspect-[4/3] overflow-hidden" data-id="element-975">
              <img src={DETAIL_IMAGE_1} alt="Custom Interior Configuration" className="absolute inset-0 w-full h-full object-cover" data-id="element-976" />
            </div>
          </div>
        </div>
      </section>

      {/* 4. DEEP DIVE 2: TAILORED TO YOU */}
      <section className="py-20 md:py-32 bg-white" data-id="element-977">
        <div className="container mx-auto px-6 md:px-12" data-id="element-978">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-979">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-980">
              <img src={DETAIL_IMAGE_2} alt="Material Selection" className="absolute inset-0 w-full h-full object-cover" data-id="element-981" />
            </div>
            <div data-id="element-982">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-983">
                Personalization
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-984">
                Tailored to Your Adventure
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-985">
                <p data-id="element-986">
                  Are you a winter sports enthusiast? We can equip your van with
                  a specialized diesel heating system and heated tanks. Do you
                  work from the road? We can install a lithium power bank and a
                  dedicated workspace.
                </p>
                <p data-id="element-987">
                  Our "Custom Line" allows for even deeper personalization. If
                  you have a specific vision that isn't on the options list,
                  talk to us. Our craftsmen love a challenge.
                </p>
                <p data-id="element-988">
                  Your Robeta isn't just a product; it's a collaboration between
                  our engineers and your imagination.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. KEY BENEFITS GRID */}
      <section className="py-24 bg-robeta-black text-white" data-id="element-989">
        <div className="container mx-auto px-6 md:px-12" data-id="element-990">
          <div className="text-center mb-16" data-id="element-991">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-editorial mb-4" data-id="element-992">
              Infinite Choice
            </h2>
            <div className="w-24 h-1 bg-robeta-copper mx-auto" data-id="element-993" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-994">
            {[{
            icon: Palette,
            title: '7 Wood Finishes',
            desc: 'From classic warm oaks to modern industrial greys and stones.'
          }, {
            icon: Sofa,
            title: '30+ Upholsteries',
            desc: 'A vast selection of fabrics, vegan leathers, and genuine hides.'
          }, {
            icon: PaintBucket,
            title: 'Exterior Colors',
            desc: 'Full range of Mercedes-Benz factory colors and custom graphics.'
          }, {
            icon: LayoutGrid,
            title: 'Layout Options',
            desc: 'Multiple bathroom, kitchen, and sleeping configurations available.'
          }].map((item, i) => <div key={i} className="bg-white/5 p-8 border border-white/10 hover:border-robeta-copper/50 transition-colors" data-id="element-995">
                <item.icon className="w-10 h-10 text-robeta-copper mb-6" data-id="element-996" />
                <h3 className="text-xl font-bold uppercase tracking-wide mb-4" data-id="element-997">
                  {item.title}
                </h3>
                <p className="text-white/60 font-light leading-relaxed text-sm" data-id="element-998">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. NEXT PILLAR NAV */}
      <section className="border-t border-robeta-black/10 bg-white" data-id="element-999">
        <div className="container mx-auto px-6 md:px-12 py-16 md:py-24" data-id="element-1000">
          <div className="flex flex-col items-center text-center" data-id="element-1001">
            <span className="text-robeta-black/40 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-1002">
              Next Pillar
            </span>
            <Link to="/robeta-difference/signature-craftsmanship" className="group flex flex-col items-center" data-id="element-1003">
              <h2 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial mb-6 group-hover:text-robeta-copper transition-colors" data-id="element-1004">
                Signature Craftsmanship
              </h2>
              <div className="flex items-center text-sm font-bold uppercase tracking-widest text-robeta-black group-hover:text-robeta-copper transition-colors" data-id="element-1005">
                Meet the Makers <ArrowRight className="ml-2 w-4 h-4" data-id="element-1006" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. CTA SECTION */}
      <section className="py-28 md:py-36 bg-robeta-mist/20 text-center" data-id="element-1007">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1008">
          <h2 className="text-4xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-1009">
            Start Designing
          </h2>
          <p className="text-lg text-robeta-black/60 font-light max-w-2xl mx-auto mb-12" data-id="element-1010">
            Visit a dealer to see samples and start configuring your dream van.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-1011">
            <Link to="/dealer-network" className="px-10 py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors min-w-[200px]" data-id="element-1012">
              Find a Dealer
            </Link>
            <Link to="/models" className="px-10 py-4 border border-robeta-black text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors min-w-[200px]" data-id="element-1013">
              View Models
            </Link>
          </div>
        </div>
      </section>
    </main>;
}